import React, { useState, useEffect, useCallback } from 'react';
import { admin } from '../services/api';
import { toast } from 'react-toastify';

/**
 * Admin panel for organizations.
 *
 * An organization owns DIDs and holds one pooled monthly SMS allowance. Every
 * member sees and can reply to all of the organization's conversations, which
 * is what lets a team share a single number.
 */
const OrganizationsPanel = ({ users = [], dids = [], onChanged }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [newOrg, setNewOrg] = useState({ name: '', smsLimit: '' });

  const load = useCallback(async () => {
    try {
      const res = await admin.listOrganizations();
      setOrganizations(res.data || []);
    } catch (error) {
      toast.error('Could not load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mark = (key, value) => setBusy(prev => ({ ...prev, [key]: value }));

  const refresh = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newOrg.name.trim()) return;
    try {
      mark('create', true);
      await admin.createOrganization({
        name: newOrg.name.trim(),
        smsLimit: newOrg.smsLimit === '' ? null : parseInt(newOrg.smsLimit, 10)
      });
      toast.success(`Organization "${newOrg.name.trim()}" created`);
      setNewOrg({ name: '', smsLimit: '' });
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not create organization');
    } finally {
      mark('create', false);
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? This cannot be undone.`)) return;
    try {
      mark(org._id, true);
      await admin.deleteOrganization(org._id);
      toast.success('Organization deleted');
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not delete organization');
    } finally {
      mark(org._id, false);
    }
  };

  const handleLimit = async (org, value) => {
    try {
      mark(org._id, true);
      await admin.updateOrganization(org._id, {
        smsLimit: value === '' ? null : parseInt(value, 10)
      });
      toast.success('SMS limit updated');
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not update limit');
    } finally {
      mark(org._id, false);
    }
  };

  const handleAddMember = async (org, userId) => {
    if (!userId) return;
    try {
      mark(org._id, true);
      await admin.addOrganizationMember(org._id, userId);
      toast.success('Member added');
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not add member');
    } finally {
      mark(org._id, false);
    }
  };

  const handleRemoveMember = async (org, user) => {
    if (!window.confirm(`Remove ${user.username} from ${org.name}? They will get their own personal organization.`)) return;
    try {
      mark(org._id, true);
      await admin.removeOrganizationMember(org._id, user._id);
      toast.success('Member removed');
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not remove member');
    } finally {
      mark(org._id, false);
    }
  };

  const handleAssignDid = async (org, didId) => {
    if (!didId) return;
    try {
      mark(org._id, true);
      const res = await admin.assignOrganizationDID(org._id, didId);
      const moved = res?.data?.messagesUpdated || 0;
      toast.success(`DID assigned${moved ? ` — ${moved} message(s) now shared` : ''}`);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not assign DID');
    } finally {
      mark(org._id, false);
    }
  };

  const handleUnassignDid = async (org, did) => {
    if (!window.confirm(`Remove ${did.number} from ${org.name}? Members will lose access to its conversations.`)) return;
    try {
      mark(org._id, true);
      await admin.unassignOrganizationDID(org._id, did._id);
      toast.success('DID unassigned');
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not unassign DID');
    } finally {
      mark(org._id, false);
    }
  };

  const membersOf = (orgId) => users.filter(u => String(u.organization) === String(orgId));
  const unassignedDids = dids.filter(d => !d.organization);

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="animate-pulse text-gray-500">Loading organizations…</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-semibold text-gray-800">Organizations</h2>
        <span className="text-sm text-gray-500">{organizations.length} total</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Members of an organization share its DIDs — they all see the same conversations
        and can reply to any of them. Each message shows who sent it.
      </p>

      {/* Create */}
      <form onSubmit={handleCreate} className="flex flex-wrap gap-2 items-end mb-6 pb-6 border-b">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Organization name</label>
          <input
            type="text"
            value={newOrg.name}
            onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
            placeholder="Acme Corp"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00a884]"
          />
        </div>
        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monthly SMS limit
          </label>
          <input
            type="number"
            min="0"
            value={newOrg.smsLimit}
            onChange={(e) => setNewOrg({ ...newOrg, smsLimit: e.target.value })}
            placeholder="Unlimited"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00a884]"
          />
        </div>
        <button
          type="submit"
          disabled={busy.create || !newOrg.name.trim()}
          className="px-4 py-2 bg-[#00a884] text-white rounded-md hover:bg-[#008f72] disabled:opacity-50"
        >
          {busy.create ? 'Creating…' : 'Create'}
        </button>
      </form>

      {organizations.length === 0 && (
        <p className="text-gray-500 text-sm">No organizations yet.</p>
      )}

      <div className="space-y-3">
        {organizations.map(org => {
          const members = membersOf(org._id);
          const orgDids = dids.filter(d => String(d.organization) === String(org._id));
          const isOpen = expanded === org._id;
          const used = org.smsUsage?.monthly || 0;

          return (
            <div key={org._id} className="border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpanded(isOpen ? null : org._id)}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{org.name}</span>
                    {org.isPersonal && (
                      <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                        personal
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {members.length} member{members.length === 1 ? '' : 's'} ·{' '}
                    {orgDids.length} DID{orgDids.length === 1 ? '' : 's'} ·{' '}
                    {org.smsLimit === null || org.smsLimit === undefined
                      ? `${used} sent (unlimited)`
                      : `${used} / ${org.smsLimit} this month`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(org); }}
                    disabled={busy[org._id]}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    Delete
                  </button>
                  <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                  {/* Quota */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pooled monthly SMS limit
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        defaultValue={org.smsLimit === null ? '' : org.smsLimit}
                        placeholder="Unlimited"
                        onBlur={(e) => {
                          const v = e.target.value;
                          const current = org.smsLimit === null ? '' : String(org.smsLimit);
                          if (v !== current) handleLimit(org, v);
                        }}
                        className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00a884]"
                      />
                      <span className="text-sm text-gray-500 self-center">
                        shared by all members · leave blank for unlimited
                      </span>
                    </div>
                  </div>

                  {/* Members */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Members</h4>
                    {members.length === 0 && (
                      <p className="text-sm text-gray-500 mb-2">No members yet.</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {members.map(m => (
                        <span key={m._id} className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-full px-3 py-1 text-sm">
                          {m.username}
                          {m.role === 'admin' && (
                            <span className="text-[10px] uppercase text-blue-600">admin</span>
                          )}
                          <button
                            onClick={() => handleRemoveMember(org, m)}
                            disabled={busy[org._id]}
                            className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                            aria-label={`Remove ${m.username}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <select
                      value=""
                      onChange={(e) => handleAddMember(org, e.target.value)}
                      disabled={busy[org._id]}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#00a884]"
                    >
                      <option value="">Add a member…</option>
                      {users
                        .filter(u => String(u.organization) !== String(org._id))
                        .map(u => (
                          <option key={u._id} value={u._id}>{u.username}</option>
                        ))}
                    </select>
                  </div>

                  {/* DIDs */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Shared numbers</h4>
                    {orgDids.length === 0 && (
                      <p className="text-sm text-gray-500 mb-2">No DIDs assigned.</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {orgDids.map(d => (
                        <span key={d._id} className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-full px-3 py-1 text-sm font-mono">
                          {d.number}
                          <button
                            onClick={() => handleUnassignDid(org, d)}
                            disabled={busy[org._id]}
                            className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                            aria-label={`Unassign ${d.number}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <select
                      value=""
                      onChange={(e) => handleAssignDid(org, e.target.value)}
                      disabled={busy[org._id]}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#00a884]"
                    >
                      <option value="">Assign a DID…</option>
                      {unassignedDids.map(d => (
                        <option key={d._id} value={d._id}>{d.number}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Assigning a DID also moves its existing conversation history into this organization.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrganizationsPanel;
